import { useEffect } from 'react';

type SeoStructuredData =
  | Record<string, unknown>
  | readonly Record<string, unknown>[];

export type PageSeoConfig = {
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string;
  canonicalPath: string;
  description: string;
  imageAlt?: string;
  imageHeight?: string;
  imagePath?: string;
  imageType?: string;
  imageWidth?: string;
  keywords?: string;
  locale?: string;
  modifiedAt?: string;
  ogType?: 'article' | 'website';
  publishedAt?: string;
  robots?: string;
  siteName?: string;
  structuredData?: SeoStructuredData;
  title: string;
  twitterData1?: string;
  twitterLabel1?: string;
};

type DefaultSeoConfig = {
  description: string;
  imageAlt: string;
  imagePath?: string;
  keywords: string;
  title: string;
  twitterDescription: string;
};

const structuredDataId = 'datasliv-page-structured-data';
const pageSeoMode = 'custom';

const normalizePath = (path: string) => {
  if (/^(https?:\/\/|data:image\/|blob:)/i.test(path)) {
    return path;
  }

  return path.startsWith('/') ? path : `/${path}`;
};

const getAbsoluteUrl = (path: string) => {
  const normalizedPath = normalizePath(path);

  if (/^(https?:\/\/|data:image\/|blob:)/i.test(normalizedPath)) {
    return normalizedPath;
  }

  if (typeof window === 'undefined') {
    return normalizedPath;
  }

  return `${window.location.origin}${normalizedPath}`;
};

const upsertMeta = (
  attribute: 'name' | 'property',
  selectorValue: string,
  content: string
) => {
  let meta = document.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${selectorValue}"]`
  );

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, selectorValue);
    document.head.appendChild(meta);
  }

  meta.content = content;
};

const removeMeta = (attribute: 'name' | 'property', selectorValue: string) => {
  document
    .querySelector<HTMLMetaElement>(`meta[${attribute}="${selectorValue}"]`)
    ?.remove();
};

const upsertCanonical = (href: string) => {
  let canonical = document.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }

  canonical.href = href;
};

const upsertStructuredData = (structuredData: SeoStructuredData) => {
  let script = document.getElementById(
    structuredDataId
  ) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = structuredDataId;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(structuredData);
};

const removeStructuredData = () => {
  document.getElementById(structuredDataId)?.remove();
};

const setCustomPageSeoMode = () => {
  document.documentElement.dataset.pageSeo = pageSeoMode;
};

const clearCustomPageSeoMode = () => {
  if (document.documentElement.dataset.pageSeo === pageSeoMode) {
    delete document.documentElement.dataset.pageSeo;
  }
};

const formatOgLocale = (locale: string | undefined) =>
  locale?.replace('-', '_') ?? 'en_US';

const removeArticleSeo = () => {
  removeMeta('property', 'article:published_time');
  removeMeta('property', 'article:modified_time');
  removeMeta('property', 'article:author');
  removeMeta('property', 'article:section');
  removeMeta('property', 'article:tag');
};

const removeImageDetails = () => {
  removeMeta('property', 'og:image:type');
  removeMeta('property', 'og:image:width');
  removeMeta('property', 'og:image:height');
};

const removeTwitterLabels = () => {
  removeMeta('name', 'twitter:label1');
  removeMeta('name', 'twitter:data1');
};

const applyDefaultSeo = (defaultSeo: DefaultSeoConfig) => {
  const canonicalUrl = getAbsoluteUrl('/');
  const imageUrl = getAbsoluteUrl(
    defaultSeo.imagePath ?? '/images/logo-transparent.png'
  );

  document.title = defaultSeo.title;
  upsertCanonical(canonicalUrl);
  upsertMeta('name', 'description', defaultSeo.description);
  upsertMeta('name', 'keywords', defaultSeo.keywords);
  upsertMeta('name', 'robots', 'index, follow');
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:locale', 'en_US');
  upsertMeta('property', 'og:site_name', 'DataSliv');
  upsertMeta('property', 'og:title', defaultSeo.title);
  upsertMeta('property', 'og:description', defaultSeo.description);
  upsertMeta('property', 'og:url', canonicalUrl);
  upsertMeta('property', 'og:image', imageUrl);
  upsertMeta('property', 'og:image:secure_url', imageUrl);
  upsertMeta('property', 'og:image:alt', defaultSeo.imageAlt);
  upsertMeta('name', 'twitter:card', 'summary');
  upsertMeta('name', 'twitter:title', defaultSeo.title);
  upsertMeta('name', 'twitter:description', defaultSeo.twitterDescription);
  upsertMeta('name', 'twitter:image', imageUrl);
  upsertMeta('name', 'twitter:image:alt', defaultSeo.imageAlt);
  removeArticleSeo();
  removeImageDetails();
  removeTwitterLabels();
  removeStructuredData();
};

export const usePageSeo = (
  config: PageSeoConfig | null,
  defaultSeo: DefaultSeoConfig
) => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (!config) {
      clearCustomPageSeoMode();
      applyDefaultSeo(defaultSeo);
      return undefined;
    }

    setCustomPageSeoMode();

    const canonicalUrl = getAbsoluteUrl(config.canonicalPath);
    const imageUrl = config.imagePath
      ? getAbsoluteUrl(config.imagePath)
      : undefined;

    document.title = config.title;
    upsertCanonical(canonicalUrl);
    upsertMeta('name', 'description', config.description);
    upsertMeta('name', 'keywords', config.keywords ?? '');
    upsertMeta('name', 'robots', config.robots ?? 'index, follow');
    upsertMeta('property', 'og:type', config.ogType ?? 'website');
    upsertMeta('property', 'og:locale', formatOgLocale(config.locale));
    upsertMeta('property', 'og:site_name', config.siteName ?? 'DataSliv');
    upsertMeta('property', 'og:title', config.title);
    upsertMeta('property', 'og:description', config.description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta(
      'name',
      'twitter:card',
      imageUrl ? 'summary_large_image' : 'summary'
    );
    upsertMeta('name', 'twitter:title', config.title);
    upsertMeta('name', 'twitter:description', config.description);

    if (imageUrl) {
      upsertMeta('property', 'og:image', imageUrl);
      upsertMeta('property', 'og:image:secure_url', imageUrl);
      upsertMeta('name', 'twitter:image', imageUrl);
    } else {
      removeMeta('property', 'og:image');
      removeMeta('property', 'og:image:secure_url');
      removeMeta('name', 'twitter:image');
    }

    if (config.imageAlt) {
      upsertMeta('property', 'og:image:alt', config.imageAlt);
      upsertMeta('name', 'twitter:image:alt', config.imageAlt);
    } else {
      removeMeta('property', 'og:image:alt');
      removeMeta('name', 'twitter:image:alt');
    }

    if (config.imageType) {
      upsertMeta('property', 'og:image:type', config.imageType);
    } else {
      removeMeta('property', 'og:image:type');
    }

    if (config.imageWidth) {
      upsertMeta('property', 'og:image:width', config.imageWidth);
    } else {
      removeMeta('property', 'og:image:width');
    }

    if (config.imageHeight) {
      upsertMeta('property', 'og:image:height', config.imageHeight);
    } else {
      removeMeta('property', 'og:image:height');
    }

    if (config.publishedAt) {
      upsertMeta('property', 'article:published_time', config.publishedAt);
    } else {
      removeMeta('property', 'article:published_time');
    }

    if (config.modifiedAt) {
      upsertMeta('property', 'article:modified_time', config.modifiedAt);
    } else {
      removeMeta('property', 'article:modified_time');
    }

    if (config.articleAuthor) {
      upsertMeta('property', 'article:author', config.articleAuthor);
    } else {
      removeMeta('property', 'article:author');
    }

    if (config.articleSection) {
      upsertMeta('property', 'article:section', config.articleSection);
    } else {
      removeMeta('property', 'article:section');
    }

    if (config.articleTags) {
      upsertMeta('property', 'article:tag', config.articleTags);
    } else {
      removeMeta('property', 'article:tag');
    }

    if (config.twitterLabel1 && config.twitterData1) {
      upsertMeta('name', 'twitter:label1', config.twitterLabel1);
      upsertMeta('name', 'twitter:data1', config.twitterData1);
    } else {
      removeTwitterLabels();
    }

    if (config.structuredData) {
      upsertStructuredData(config.structuredData);
    } else {
      removeStructuredData();
    }

    return () => {
      clearCustomPageSeoMode();
      applyDefaultSeo(defaultSeo);
    };
  }, [config, defaultSeo]);
};

export { getAbsoluteUrl };
